import { PUBLISHED_RASTER_F1_RANGE, RASTER_GATE_F1_CLEAN, RASTER_GATE_F1_NOISY } from '@drawing2d/core';

/**
 * What the shipped detector actually scores, stated on the surface that uses it.
 *
 * These are not aspirations and not placeholders. Every figure is copied from
 * `output/drawing2d-raster/0.1.0/report.json`, produced by `npm run
 * acceptance:raster` on the held-out drawing at all three DPIs, clean and noisy —
 * and `raster-accuracy.test.ts` reads that report and fails if any of them drifts.
 * A hand-maintained number beside a generated one is a lie waiting for a
 * re-baseline; the test is what stops it.
 *
 * The reason they are here at all is `docs/audit/2026-08-10-first-use-register.md`:
 * a number the interface presents wrongly is worse than one it does not present.
 * The scan surface therefore shows the measured figure, the bar it is held to, the
 * published range for comparable systems, and what the figure does not cover.
 *
 * **The gate passes now, and that raises the opposite risk to the one this file
 * was written for.** A surface reporting 1.000 is a surface a reader will trust
 * further than the measurement earns, so two things are published beside it and
 * neither is optional: the corpus is **synthetic** — four drawings, degraded
 * deterministically, no customer sheet anywhere — and `everyImageWorstF1` is the
 * worst row over *every* image rather than the held-out ones the bar is applied
 * to. Printing only the gated figure would be the same defect as printing 0.818
 * with no bar beside it, arriving from the other direction.
 *
 * **Every figure here is now the WORST gated row of its type, not the only one.**
 * Until ADR 0011 the gated rows of a type were identical, so one number described
 * them and the test asserted exactly that. Per-type tolerances ended it: `LINE` is
 * matched at 0.535 pt rather than the 0.691 pt it used to share with `CIRCLE`, and
 * at that tolerance the `noisy 450` row falls to 0.889 while the rest hold at
 * 1.000. A surface that kept quoting 1.000 would be overstating by the width of
 * the row it stopped mentioning.
 */
export const MEASURED_RASTER_ACCURACY = {
  detector: 'baseline-hough',
  /** Regenerate with `npm run acceptance:raster`. */
  report: 'output/drawing2d-raster/0.1.0/summary.md',
  /** Identifies the corpus these figures were measured on, not the commit. */
  corpusManifestHash: 'ef1b51c6943d52e5',

  /**
   * The worst gated row, held out, over every DPI and both variants.
   *
   * It is `noisy 450`, and what moved it is the tolerance rather than the
   * detector: all 36 truth rules on the four 450 noisy sheets are still found and
   * still located within the old 0.691 pt, and three of them are not within the
   * new 0.535 pt. Recall is defined against the tolerance, so it falls with it.
   */
  worstGatedLinePrecision: 0.889,
  worstGatedLineRecall: 0.889,
  worstGatedLineF1: 0.889,
  /**
   * Circles are detected now, and by fitting components rather than by a Hough
   * circle transform. Zero was a measurement when the detector attempted none;
   * this is a measurement too, on the same corpus.
   */
  worstGatedCircleF1: 1.0,
  /**
   * The drawing frame and the title block, which is the new one.
   *
   * Closed polylines were unscored until ADR 0011 — deliberately, because with a
   * shared tolerance scoring them loosened lines and circles — and the title block
   * was missed on all 24 images for as long as that was true, with nothing in the
   * repository able to report it. Two entities per image is a small sample and the
   * surface says so; it is here because an unmeasured recognition is one that can
   * regress in silence.
   */
  worstGatedPolylineF1: 1.0,
  macroF1: 0.994,
  passed: true,

  /**
   * The worst row over every image in the corpus, not only the held-out ones.
   *
   * The gate is applied to the holdout, which is the right split to gate on. This
   * figure is the other half of the sentence, and it exists because the dev rows
   * were computed and printed nowhere for a whole build while a real defect sat
   * in them. What separates it from 1.000 is a deliberately unsupported freeform
   * Bézier in one fixture, which the detector reports a line through because it is
   * real ink the contract has no entity for, and — since the tolerance tightened —
   * three rules on degraded 450 DPI sheets that are located to better than 0.7 pt
   * but not to better than 0.535 pt.
   */
  everyImageWorstF1: 0.904,

  /**
   * Four synthetic drawings, degraded deterministically. No customer drawing is
   * used here or anywhere, which is a licensing and privacy property first — and
   * a limit on what the figure above can be read to mean second.
   */
  corpusDrawings: 4,
  corpusSynthetic: true,

  cleanBar: RASTER_GATE_F1_CLEAN,
  noisyBar: RASTER_GATE_F1_NOISY,
  publishedRange: PUBLISHED_RASTER_F1_RANGE,
} as const;
